// Overview phases → Sent Items URLs.
//
// The Overview's workflow lanes are served by GET /api/sent-items/lanes and
// counted by the SERVER over the same rows the list serves, by one rule
// (apps/api/src/lib/sentItems/phase.ts: `phaseOfRow`). The list filters by the
// same rule through `phase=`. So a segment's href is just the lane's own
// `select` plus the phase — nothing here re-derives what a phase means, which
// is how the last table went wrong: it mapped `ready` to `step=generated` while
// the lane counted "ready" with different rules, and Intake "7 ready" opened an
// empty list (2026-08-29).
//
// `view=all` is stated on every drill-down: the counts are over the whole
// corpus, and the page otherwise re-applies the operator's remembered view
// (sentItemsView.ts) — a "closed" segment under the remembered "Who's waiting"
// view would show nothing.

import { itemKindForThreadType, SENT_ITEMS_PATH } from '@/lib/sentItemIds';
import {
  OVERVIEW_PHASES,
  TYPE_GROUP_BY_KIND,
  type OverviewPhase,
  type SentItemsLane,
  type SentItemsLaneSelect,
} from '@/lib/sentItemsClient';

export { OVERVIEW_PHASES };
export type { OverviewPhase };

function isOverviewPhase(value: string | null | undefined): value is OverviewPhase {
  return !!value && (OVERVIEW_PHASES as readonly string[]).includes(value);
}

/** A lane's `select`, written as Sent Items URL params (sentItemsView.ts's vocabulary). */
function applyLaneSelect(search: URLSearchParams, select: SentItemsLaneSelect): void {
  search.set('types', select.types);
  if (select.renewals === 'only') search.set('renewals', '1');
  if (select.renewals === 'exclude') search.set('renewals', '0');
}

/**
 * Write a phase onto `search`, in place. An unknown phase writes nothing — a
 * hand-edited `?phase=nonsense` should show the unfiltered list rather than a
 * filter nobody can name. Returns whether the phase was recognised.
 */
export function applyPhaseFilter(
  search: URLSearchParams,
  phase: string | null | undefined,
): boolean {
  if (!isOverviewPhase(phase)) return false;
  search.set('phase', phase);
  search.set('view', 'all');
  return true;
}

/** The Sent Items href for one lane, optionally narrowed to a phase. */
export function sentItemsHrefForLane(lane: SentItemsLane, phase?: string | null): string {
  const next = new URLSearchParams();
  applyLaneSelect(next, lane.select);
  applyPhaseFilter(next, phase);
  next.set('view', 'all');
  return `${SENT_ITEMS_PATH}?${next.toString()}`;
}

/**
 * The Sent Items href for an old `/agreements?type=…&phase=…` link (the
 * redirect in itemRedirects.tsx). `threadType` is the old thread type; it
 * becomes the §6.2 chip group through the row kind, so `renewal` lands on the
 * Lease chip narrowed to renewals (a renewal is a lease row carrying
 * `isRenewal`, not a type of its own).
 */
export function sentItemsHrefForPhase(
  threadType: string | null | undefined,
  phase?: string | null,
  extra?: { q?: string | null },
): string {
  const next = new URLSearchParams();
  const rowKind = itemKindForThreadType(threadType);
  if (rowKind) {
    applyLaneSelect(next, {
      types: TYPE_GROUP_BY_KIND[rowKind],
      ...(threadType?.trim() === 'renewal' ? { renewals: 'only' as const } : {}),
    });
  }
  applyPhaseFilter(next, phase);
  if (extra?.q) next.set('q', extra.q);
  const qs = next.toString();
  return qs ? `${SENT_ITEMS_PATH}?${qs}` : SENT_ITEMS_PATH;
}
